// Network resilience helpers for /api/* requests.
//
// Two responsibilities:
//   1) Retry transient axios failures (network errors, no response) with
//      exponential backoff. Safe for any HTTP method because a missing
//      response means the request never reached the server.
//   2) Broadcast online/offline + last-attempt status to subscribers so the
//      <OfflineToast> can render a banner without tight coupling.
//
// Server 5xx responses are NOT retried (the server got the request and chose
// to error — silent retries could double-charge or duplicate writes).

import axios from "axios";

const MAX_RETRIES = 3;
// 600ms, 1.2s, 2.4s — capped at 3s
const baseDelay = (attempt) => Math.min(3000, 600 * Math.pow(2, attempt));

const listeners = new Set();
let lastStatus = {
  online: typeof navigator !== "undefined" ? navigator.onLine : true,
  apiFailing: false, // true once a request has retried at least once and not yet succeeded
};

function emit() {
  for (const fn of listeners) {
    try { fn(lastStatus); } catch (e) { console.error(e); }
  }
}

function update(patch) {
  const next = { ...lastStatus, ...patch };
  if (next.online !== lastStatus.online || next.apiFailing !== lastStatus.apiFailing) {
    lastStatus = next;
    emit();
  }
}

export function getNetworkStatus() {
  return lastStatus;
}

export function subscribeNetworkStatus(fn) {
  listeners.add(fn);
  // Fire immediately so subscribers can render initial state
  try { fn(lastStatus); } catch (e) { console.error(e); }
  return () => listeners.delete(fn);
}

function isApiUrl(url) {
  if (!url) return false;
  // Match either absolute (https://.../api/...) or relative (/api/...)
  return /\/api\//.test(url);
}

function isRetryableError(error) {
  // Network error (no response) or request was canceled by browser due to network drop
  if (!error?.response) return true;
  // Treat 502/503/504 as retryable for GETs only (server temporarily unreachable
  // through the proxy). POST/PATCH/DELETE not retried on 5xx — could duplicate writes.
  const status = error.response.status;
  const method = (error.config?.method || "get").toLowerCase();
  if (method === "get" && (status === 502 || status === 503 || status === 504)) return true;
  return false;
}

export function setupAxiosRetry() {
  axios.interceptors.response.use(
    (response) => {
      if (isApiUrl(response.config?.url)) {
        update({ apiFailing: false });
      }
      return response;
    },
    async (error) => {
      const config = error.config;
      // Only retry our own /api/* calls
      if (!config || !isApiUrl(config.url)) return Promise.reject(error);

      // The server SENT a response (any status) — connectivity is fine. Clear
      // any stale `apiFailing` flag from earlier failed calls. This is the key
      // fix: 404/401/403/422 etc. all prove the network reaches the server, so
      // they should dismiss the "Reconnecting…" toast.
      if (error.response) {
        update({ apiFailing: false });
      }

      // Auth-related calls — let the caller handle 401/403 immediately
      if (error.response && [401, 403].includes(error.response.status)) {
        return Promise.reject(error);
      }
      if (!isRetryableError(error)) return Promise.reject(error);

      config.__retryCount = (config.__retryCount || 0);
      if (config.__retryCount >= MAX_RETRIES) {
        update({ apiFailing: true });
        return Promise.reject(error);
      }
      config.__retryCount += 1;
      // First retry → flip the failing flag so the toast surfaces
      if (config.__retryCount === 1) update({ apiFailing: true });
      const delay = baseDelay(config.__retryCount - 1);
      await new Promise((r) => setTimeout(r, delay));
      return axios(config);
    },
  );

  // Wire browser online/offline events
  if (typeof window !== "undefined") {
    window.addEventListener("online", () => update({ online: true, apiFailing: false }));
    window.addEventListener("offline", () => update({ online: false }));
  }
}
